import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Fab,
  IconButton,
  Paper,
  Slide,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
  createFilterOptions,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { listFilterPosts, searchPostsAdvanced } from '../../services/PostService';
import { REMOTE_NATION, useCategories, useNations } from '../../common/hooks/useReferenceData';
import useAuth from '../../common/hooks/useAuth';
import { regionName } from '../../utils/utils';
import { detailsPath } from '../posts/PostComponent';
import { brand } from '../../theme';

// Pages with their own bottom action bar / form: the floating button would cover it on phones.
const HIDE_ON_MOBILE = ['/details', '/candidate', '/add-post', '/edit-post', '/talent-user-direct-contact', '/login', '/register', '/forgot-password', '/reset-password', '/manage-user', '/manage-talent-user'];

const BotBubble = ({ children }) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start">
    <Avatar sx={{ width: 30, height: 30, background: brand.gradient }}><AutoAwesomeRoundedIcon sx={{ fontSize: 16 }} /></Avatar>
    <Paper elevation={0} sx={{ px: 2, py: 1.5, borderRadius: '4px 18px 18px 18px', backgroundColor: '#FFF', border: `1px solid ${brand.border}`, maxWidth: '85%' }}>
      {children}
    </Paper>
  </Stack>
);

const UserBubble = ({ children }) => (
  <Stack direction="row" justifyContent="flex-end">
    <Paper elevation={0} sx={{ px: 2, py: 1.25, borderRadius: '18px 4px 18px 18px', color: '#FFF', background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)', maxWidth: '80%' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{children}</Typography>
    </Paper>
  </Stack>
);

export default function AICareerAssistant() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'en';
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuth } = useAuth();
  const categories = useCategories();
  const nations = useNations();

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: role, 2: location, 3: results
  const [role, setRole] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [place, setPlace] = useState(null); // null = everywhere, REMOTE_NATION or a nation
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  const suggestions = categories.length
    ? categories.slice(0, 6).map((c) => c.categoryName)
    : ['IT & Software', 'Marketing', 'Finance', 'Customer Care', 'Engineering', 'Sales'];

  const nationOptions = useMemo(
    () => nations.map((n) => ({ ...n, _label: regionName(n.nationTag, lang, n.nationName) || n.nationName })).sort((a, b) => a._label.localeCompare(b._label, lang)),
    [nations, lang]
  );
  const filterNations = useMemo(() => createFilterOptions({ stringify: (o) => `${o._label} ${o.nationName} ${o.nationTag}` }), []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [step, loading, jobs, isOpen]);

  // Close the panel when navigating to another page on phones.
  useEffect(() => {
    if (isMobile) setIsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Not useful in the admin area (and it would cover the users table).
  const hidden = location.pathname.startsWith('/admin') || (isMobile && HIDE_ON_MOBILE.some((p) => location.pathname.startsWith(p)));
  if (hidden && !isOpen) return null;

  const chooseRole = (value) => {
    const v = value.trim();
    if (!v) return;
    setRole(v);
    setStep(2);
  };

  const runSearch = async (selectedPlace) => {
    setPlace(selectedPlace);
    setStep(3);
    setLoading(true);
    const remote = selectedPlace === REMOTE_NATION;
    const filters = {
      keyword: role,
      remote: remote || undefined,
      nationTag: !remote && selectedPlace ? selectedPlace.nationTag : undefined,
      nationId: !remote && selectedPlace ? String(selectedPlace.nationId) : undefined,
      nationName: !remote && selectedPlace ? selectedPlace.nationName : undefined,
    };
    try {
      let results = [];
      try {
        const res = await searchPostsAdvanced(filters, 0, 6, 'insertData', 'desc');
        results = res.data?.content || [];
      } catch {
        const res = await listFilterPosts(role, 0, 20, false, 'insertData', 'desc');
        results = (res.data?.content || []).filter((j) => (remote ? j.remote : !selectedPlace || String(j.nationTag || '').toLowerCase() === String(selectedPlace.nationTag).toLowerCase()));
      }
      setJobs(results.slice(0, 6));
    } catch {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep(1);
    setRole('');
    setCustomRole('');
    setPlace(null);
    setJobs([]);
  };

  const viewAll = () => {
    navigate('/posts', { state: { textSearch: role, nation: place === REMOTE_NATION ? null : place, isRemote: place === REMOTE_NATION } });
    setIsOpen(false);
  };

  const placeLabel = place === REMOTE_NATION ? t('chat.remoteOnly') : place ? regionName(place.nationTag, lang, place.nationName) : t('chat.allCountries');

  return (
    <>
      {!hidden && (
        <Box
          sx={{
            position: 'fixed',
            right: { xs: 16, md: 28 },
            bottom: { xs: 'calc(var(--app-bottom-nav-height) + var(--safe-bottom) + 16px)', md: 28 },
            // Below the app bar / drawers / dialogs, so it never covers an open sheet or menu.
            zIndex: 1050,
          }}
        >
          <Tooltip title={t('chat.tooltip')} placement="left">
            <Fab
              variant={isMobile || isOpen ? 'circular' : 'extended'}
              onClick={() => setIsOpen((o) => !o)}
              aria-label={t('chat.tooltip')}
              sx={{
                color: '#FFF',
                background: brand.gradient,
                boxShadow: '0 14px 30px -10px rgba(79,70,229,0.7)',
                width: isMobile || isOpen ? 56 : 'auto',
                height: 56,
                px: isMobile || isOpen ? 0 : 2.5,
                '&:hover': { background: brand.gradient, filter: 'brightness(1.08)' },
              }}
            >
              {isOpen ? <CloseRoundedIcon /> : <AutoAwesomeRoundedIcon sx={{ mr: isMobile ? 0 : 1 }} />}
              {!isMobile && !isOpen && <Typography component="span" sx={{ fontWeight: 800, textTransform: 'none' }}>{t('chat.badge')}</Typography>}
            </Fab>
          </Tooltip>
        </Box>
      )}

      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={0}
          role="dialog"
          aria-label={t('chat.title')}
          sx={{
            position: 'fixed',
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: `1px solid ${brand.border}`,
            boxShadow: '0 30px 70px -20px rgba(15,23,42,0.45)',
            ...(isMobile
              ? { left: 0, right: 0, bottom: 0, top: 'calc(var(--app-header-height) + var(--safe-top) + 8px)', borderRadius: '24px 24px 0 0' }
              : { right: 28, bottom: 100, width: 400, height: 'min(600px, calc(100vh - 140px))', borderRadius: '24px' }),
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, color: '#FFF', background: brand.gradient }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }}><AutoAwesomeRoundedIcon /></Avatar>
              <Box>
                <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>{t('chat.title')}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>{t('chat.subtitle')}</Typography>
              </Box>
            </Stack>
            <Stack direction="row">
              <Tooltip title={t('chat.restart')}>
                <IconButton onClick={restart} sx={{ color: '#FFF' }} aria-label={t('chat.restart')}><RestartAltRoundedIcon /></IconButton>
              </Tooltip>
              <IconButton onClick={() => setIsOpen(false)} sx={{ color: '#FFF' }} aria-label={t('chat.close')}><CloseRoundedIcon /></IconButton>
            </Stack>
          </Stack>

          <Box className="pyj-scroll" sx={{ flex: 1, overflowY: 'auto', p: 2, backgroundColor: '#F8FAFC', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <BotBubble><Typography variant="body2" sx={{ fontWeight: 500 }}>{t('chat.welcome')}</Typography></BotBubble>

            {step === 1 && (
              <Box sx={{ pl: 5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>{t('chat.stepRole')}</Typography>
                <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {suggestions.map((s) => (
                    <Chip key={s} label={s} clickable onClick={() => chooseRole(s)} sx={{ backgroundColor: '#FFF', border: `1px solid ${brand.border}` }} />
                  ))}
                </Stack>
                <Box component="form" onSubmit={(e) => { e.preventDefault(); chooseRole(customRole); }} sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                  <TextField size="small" fullWidth value={customRole} onChange={(e) => setCustomRole(e.target.value)} placeholder={t('chat.typeRolePlaceholder')} inputProps={{ enterKeyHint: 'send' }} sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#FFF' } }} />
                  <IconButton type="submit" disabled={!customRole.trim()} aria-label={t('chat.send')} sx={{ color: '#FFF', background: brand.gradient, '&.Mui-disabled': { background: brand.border } }}>
                    <SendRoundedIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}

            {role && <UserBubble>{role}</UserBubble>}

            {step >= 2 && (
              <BotBubble>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: step === 2 ? 1.5 : 0 }}>{t('chat.stepLocation')}</Typography>
                {step === 2 && (
                  <Stack spacing={1.25}>
                    <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
                      <Chip icon={<HomeWorkRoundedIcon />} label={t('chat.remoteOnly')} clickable onClick={() => runSearch(REMOTE_NATION)} sx={{ backgroundColor: '#ECFDF5', color: '#047857', '& .MuiChip-icon': { color: 'inherit' } }} />
                      <Chip label={t('chat.allCountries')} clickable onClick={() => runSearch(null)} />
                    </Stack>
                    <Autocomplete
                      size="small"
                      options={nationOptions}
                      getOptionLabel={(o) => o?._label || ''}
                      filterOptions={filterNations}
                      onChange={(e, v) => v && runSearch(v)}
                      autoHighlight
                      slotProps={{ popper: { sx: { zIndex: 1400 } } }}
                      renderOption={(props, option) => {
                        const { key, ...rest } = props;
                        return (
                          <Box component="li" key={option.nationId} {...rest} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <ReactCountryFlag countryCode={option.nationTag} svg style={{ width: '1.2em', height: '1.2em', borderRadius: 3 }} aria-hidden="true" />
                            {option._label}
                          </Box>
                        );
                      }}
                      renderInput={(params) => <TextField {...params} placeholder={t('jobs.searchNation')} />}
                    />
                  </Stack>
                )}
              </BotBubble>
            )}

            {step === 3 && <UserBubble>{placeLabel}</UserBubble>}

            {loading && (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pl: 5 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">{t('jobs.loading')}</Typography>
              </Stack>
            )}

            {step === 3 && !loading && (
              <>
                <BotBubble>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{jobs.length ? t('chat.matchingTitle') : t('chat.noMatches')}</Typography>
                </BotBubble>
                <Stack spacing={1.25} sx={{ pl: 5 }}>
                  {jobs.map((job) => (
                    <Card key={job.id} sx={{ borderRadius: 3, boxShadow: 'none' }}>
                      <CardActionArea
                        onClick={() => { navigate(detailsPath(job, false), { state: { post: job } }); setIsOpen(false); }}
                        sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>{job.title}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap component="div">
                            {[job.company, job.remote ? t('postDetails.remote') : regionName(job.nationTag, lang, '')].filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                        <ChevronRightRoundedIcon color="primary" />
                      </CardActionArea>
                    </Card>
                  ))}
                  <Button variant="outlined" onClick={viewAll}>{t('chat.viewAllResults')}</Button>
                </Stack>

                {!isAuth && (
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 4, textAlign: 'center', backgroundColor: '#EEF2FF', border: `1px dashed ${brand.primaryLight}` }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: brand.primaryDark, mb: 1.5 }}>{t('chat.leadTitle')}</Typography>
                    <Button variant="contained" fullWidth startIcon={<PersonAddAltRoundedIcon />} onClick={() => { navigate('/register'); setIsOpen(false); }}>
                      {t('chat.registerCta')}
                    </Button>
                    <Button size="small" sx={{ mt: 1 }} onClick={() => { navigate('/login'); setIsOpen(false); }}>{t('chat.loginCta')}</Button>
                  </Paper>
                )}
              </>
            )}
            <div ref={endRef} style={{ paddingBottom: 'var(--safe-bottom)' }} />
          </Box>
        </Paper>
      </Slide>
    </>
  );
}
